import { useEffect, useMemo, useRef, useState } from 'react';
import './navidad.css';

const PRESETS = {
  ligero: {
    snow: { flakes: 60, drift: 0.6, size: [1.5, 3.5] },
    sparkles: 18,
    garlands: ['top'],
    frost: 0.3,
    palette: ['#ffd166', '#ef476f', '#06d6a0'],
  },
  completo: {
    snow: { flakes: 120, drift: 1, size: [1.5, 4.5] },
    sparkles: 34,
    garlands: ['top', 'bottom'],
    frost: 0.45,
    palette: ['#ff5f6d', '#ffe66d', '#4ecdc4', '#76e4f7'],
  },
  oscuro: {
    snow: { flakes: 80, drift: 0.8, size: [2, 4.2] },
    sparkles: 15,
    garlands: ['bottom'],
    frost: 0.4,
    palette: ['#5c7cfa', '#23c9ff', '#f25f5c'],
  },
};

const normalizePreset = (value) => {
  const normalized = (value || '').toString().toLowerCase();
  if (normalized === 'off' || normalized === 'none') {
    return 'off';
  }
  if (PRESETS[normalized]) {
    return normalized;
  }
  return 'completo';
};

// Permite forzar el efecto desde la URL (?navidad=oscuro, ?navidad=off) para pruebas
const getQueryOverride = () => {
  if (typeof window === 'undefined') return null;

  const queryPreset = new URLSearchParams(window.location.search).get('navidad');
  if (!queryPreset) return null;

  const normalized = normalizePreset(queryPreset);
  return {
    enabled: normalized !== 'off',
    preset: normalized === 'off' ? 'completo' : normalized,
  };
};

const usePrefersReducedMotion = () => {
  const [prefers, setPrefers] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefers(mediaQuery.matches);
    const handler = () => setPrefers(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
    } else {
      mediaQuery.addListener(handler);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handler);
      } else {
        mediaQuery.removeListener(handler);
      }
    };
  }, []);

  return prefers;
};

// remoteEnabled llega desde la gestión (socket 'efectos'); la URL o window.NavidadFX lo sobrescriben
export const useNavidadController = (remoteEnabled = false) => {
  const [override, setOverride] = useState(() => getQueryOverride());
  const state = override || { enabled: !!remoteEnabled, preset: 'completo' };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const api = {
      enable: () => setOverride({ ...state, enabled: true }),
      disable: () => setOverride({ ...state, enabled: false }),
      toggle: () => setOverride({ ...state, enabled: !state.enabled }),
      setPreset: (nextPreset) => {
        const normalized = normalizePreset(nextPreset);
        setOverride({
          enabled: normalized !== 'off',
          preset: normalized === 'off' ? 'completo' : normalized,
        });
      },
      reset: () => setOverride(null),
      state: () => ({ ...state }),
    };

    window.NavidadFX = api;
    return () => {
      delete window.NavidadFX;
    };
  }, [state.enabled, state.preset]);

  return state;
};

const SnowCanvas = ({ flakes, drift, sizeRange, reduceMotion }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const flakesArray = [];
    let animationFrame;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    const createFlake = () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      radius:
        sizeRange[0] + Math.random() * (sizeRange[1] - sizeRange[0]),
      speedY: 0.5 + Math.random() * (reduceMotion ? 0.5 : 1.2),
      drift: (Math.random() - 0.5) * drift * (reduceMotion ? 0.5 : 1),
      phase: Math.random() * Math.PI * 2,
      opacity: 0.2 + Math.random() * 0.8,
    });

    for (let i = 0; i < flakes; i += 1) {
      flakesArray.push(createFlake());
    }

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#fff';

      flakesArray.forEach((flake) => {
        ctx.globalAlpha = flake.opacity;
        ctx.beginPath();
        ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2);
        ctx.fill();

        if (!reduceMotion) {
          flake.phase += 0.01;
          flake.x += Math.sin(flake.phase) * flake.drift;
        }
        flake.y += flake.speedY;

        if (flake.y > canvas.height) {
          flake.y = -5;
          flake.x = Math.random() * canvas.width;
        }
        if (flake.x > canvas.width) flake.x = 0;
        if (flake.x < 0) flake.x = canvas.width;
      });

      animationFrame = requestAnimationFrame(render);
    };

    render();

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
    };
  }, [flakes, drift, reduceMotion, sizeRange]);

  return <canvas ref={canvasRef} className="navidad-snow-canvas" aria-hidden="true" />;
};

const Garland = ({ position, palette }) => {
  const bulbs = useMemo(
    () =>
      Array.from({ length: 16 }, (_, index) => ({
        id: index,
        color: palette[index % palette.length],
        delay: Math.random() * 3,
      })),
    [palette]
  );

  return (
    <div className={`navidad-garland navidad-garland--${position}`} aria-hidden="true">
      <div className="navidad-garland-line" />
      {bulbs.map((bulb) => (
        <span
          key={`${position}-${bulb.id}`}
          className="navidad-garland-bulb"
          style={{
            '--bulb-color': bulb.color,
            animationDelay: `${bulb.delay}s`,
          }}
        />
      ))}
    </div>
  );
};

const Sparkles = ({ count }) => {
  const sparkles = useMemo(
    () =>
      Array.from({ length: count }, (_, index) => ({
        id: index,
        left: Math.random() * 100,
        top: Math.random() * 100,
        delay: Math.random() * 6,
        duration: 4 + Math.random() * 6,
        scale: 0.5 + Math.random() * 1.5,
      })),
    [count]
  );

  return (
    <div className="navidad-sparkles" aria-hidden="true">
      {sparkles.map((sparkle) => (
        <span
          key={sparkle.id}
          className="navidad-sparkle"
          style={{
            left: `${sparkle.left}%`,
            top: `${sparkle.top}%`,
            animationDelay: `${sparkle.delay}s`,
            animationDuration: `${sparkle.duration}s`,
            transform: `scale(${sparkle.scale})`,
          }}
        />
      ))}
    </div>
  );
};

const FrostOverlay = ({ opacity }) => (
  <div
    className="navidad-frost"
    style={{ opacity }}
    aria-hidden="true"
  />
);

const NavidadFX = ({ preset = 'completo' }) => {
  const reduceMotion = usePrefersReducedMotion();
  const config = PRESETS[preset] || PRESETS.completo;

  return (
    <div className="navidad-wrapper" aria-hidden="true">
      {!reduceMotion && (
        <SnowCanvas
          flakes={config.snow.flakes}
          drift={config.snow.drift}
          sizeRange={config.snow.size}
          reduceMotion={reduceMotion}
        />
      )}
      {config.garlands.includes('top') && <Garland position="top" palette={config.palette} />}
      {config.garlands.includes('bottom') && <Garland position="bottom" palette={config.palette} />}
      {!reduceMotion && config.sparkles > 0 && <Sparkles count={config.sparkles} />}
      <FrostOverlay opacity={reduceMotion ? 0.15 : config.frost} />
    </div>
  );
};

export default NavidadFX;

